import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, message, Tag, Badge, Button, Modal, Tabs, Alert, Typography, Space, Tooltip, Divider, Input } from 'antd';
import { useReactToPrint } from 'react-to-print';
import { useSelector } from 'react-redux';
import moment from 'moment';
import userAnalyticsService from '../../services/userAnalyticsService';
import DashboardLayout from './DashboardLayout';
import PageHeader from '../Common/PageHeader';
import UserBadges from '../Common/UserBadges';
import Invoice from './Invoice';
import { 
    ArrowLeftOutlined, 
    InfoCircleOutlined, 
    BulbOutlined, 
    CheckCircleOutlined, 
    QuestionCircleOutlined,
    BookOutlined,
    EyeOutlined,
    TagOutlined,
    HistoryOutlined,
    MonitorOutlined,
    UserOutlined,
    TrophyOutlined,
    CloseCircleOutlined,
    FilePdfOutlined,
    PrinterOutlined,
    SearchOutlined
} from '@ant-design/icons';

const { TabPane } = Tabs;
const { Text, Title, Paragraph } = Typography;

// Rank a lot's bidders by their highest bid, deduping repeat bids from the
// same person, so we can show backup (2nd/3rd place) bidders in case the
// winner backs out. Falls back to email for bids fetched before user_id was
// added to the API response.
const getRankedBidders = (bids) => {
    const bestBidByBidder = new Map();

    (bids || []).forEach((bid) => {
        const bidderKey = bid.user_id ?? bid.bidder_email;
        if (!bidderKey) return;

        const amount = parseFloat(bid.amount || 0);
        const existing = bestBidByBidder.get(bidderKey);
        if (!existing || amount > existing.amount) {
            bestBidByBidder.set(bidderKey, { ...bid, amount });
        }
    });

    return Array.from(bestBidByBidder.values()).sort((a, b) => b.amount - a.amount);
};

const UserAnalytics = () => {
    const [loading, setLoading] = useState(false);
    const [sentBids, setSentBids] = useState(null);
    const [receivedBids, setReceivedBids] = useState(null);
    const [selectedAuction, setSelectedAuction] = useState(null);
    const [selectedLot, setSelectedLot] = useState(null);
    const [selectedWinner, setSelectedWinner] = useState(null);
    const [selectedSeller, setSelectedSeller] = useState(null);
    const [activeTab, setActiveTab] = useState('sent');
    const [showInstructions, setShowInstructions] = useState(false);
    const [invoiceData, setInvoiceData] = useState(null);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const invoiceRef = useRef();
    const currentUser = useSelector(state => state.user.currentUser);

    // Track window width for responsive modal
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchSentBids = async () => {
        try {
            setLoading(true);
            const data = await userAnalyticsService.getSentBids();
            setSentBids(data.bids);
        } catch (error) {
            message.error('Failed to fetch sent bids');
        } finally {
            setLoading(false);
        }
    };

    const fetchReceivedBids = async () => {
        try {
            setLoading(true);
            const data = await userAnalyticsService.getReceivedBids();
            setReceivedBids(data.bids);
        } catch (error) {
            message.error('Failed to fetch received bids');
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        setSelectedAuction(null);
        setSelectedLot(null);
        setSelectedWinner(null);
        setSelectedSeller(null);
        
        // Always fetch fresh data when tab changes
        if (key === 'sent') {
            fetchSentBids();
        } else {
            fetchReceivedBids();
        }
    };

    // Initial fetch for the default tab
    useEffect(() => {
        fetchSentBids();
    }, []);

    // Invoice print handler
    const handlePrint = useReactToPrint({
        contentRef: invoiceRef,
        documentTitle: invoiceData ? `Invoice-${invoiceData.invoiceNumber}` : 'Invoice',
        onBeforeGetContent: () => {
            if (!invoiceRef.current) {
                message.error('Invoice content not ready');
                return Promise.resolve();
            }
            message.loading({ content: 'Preparing invoice...', key: 'printing', duration: 0 });
            return Promise.resolve();
        },
        onAfterPrint: () => {
            message.destroy('printing');
            message.success({ content: 'Invoice ready for printing. Please select "Save as PDF" in the print dialog.', duration: 4 });
        },
        onPrintError: (error) => {
            message.destroy('printing');
            message.error({ content: 'Failed to print invoice', duration: 2 });
            console.error('Print error:', error);
        },
        pageStyle: `
            @page {
                size: A4;
                margin: 1cm;
            }
            @media print {
                body {
                    margin: 0;
                    padding: 0;
                }
                .no-print {
                    display: none !important;
                }
            }
        `
    });

    // Generate invoice data
    const generateInvoiceData = (lot, bid, auction, isBuyerView = false) => {
        const invoiceNumber = `INV-${auction?.id || '000'}-${lot?.lot_id || '000'}-${Date.now().toString().slice(-6)}`;
        
        let buyer = {};
        let seller = {};

        if (isBuyerView) {
            // Buyer view - current user is buyer
            buyer = {
                name: currentUser ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() : 'N/A',
                email: currentUser?.email || 'N/A',
                phone: currentUser?.phone_number || 'N/A'
            };
            // Get seller info from bid data (sent from backend)
            const sellerData = bid?.seller || {};
            seller = {
                name: sellerData?.first_name || sellerData?.last_name
                    ? `${sellerData.first_name || ''} ${sellerData.last_name || ''}`.trim()
                    : auction?.seller_name || 'Auction Organizer',
                email: sellerData?.email || auction?.seller_email || 'N/A',
                phone: sellerData?.phone_number || auction?.seller_phone || 'N/A'
            };
        } else {
            // Seller view - current user is seller, bid contains buyer info
            buyer = {
                name: bid?.first_name && bid?.last_name 
                    ? `${bid.first_name} ${bid.last_name}`.trim()
                    : bid?.first_name || 'N/A',
                email: bid?.bidder_email || 'N/A',
                phone: bid?.bidder_phone || bid?.phone_number || 'N/A'
            };
            seller = {
                name: currentUser ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() : 'N/A',
                email: currentUser?.email || 'N/A',
                phone: currentUser?.phone_number || 'N/A'
            };
        }

        return {
            invoiceNumber,
            invoiceDate: bid?.created_at || new Date().toISOString(),
            buyer,
            seller,
            lot: {
                lot_number: lot?.lot_id || lot?.lot_number || 'N/A',
                title: lot?.lot_title || 'N/A',
                description: lot?.description || ''
            },
            auction: {
                title: auction?.auction_title || auction?.title || 'N/A',
                id: auction?.auction_id || auction?.id
            },
            bidAmount: bid?.amount || lot?.current_price || 0,
            tax: 0, // Can be configured later
            totalAmount: bid?.amount || lot?.current_price || 0,
            currency: 'PKR'
        };
    };

    // Handle invoice generation
    const handleGenerateInvoice = (lot, bid, auction, isBuyerView = false) => {
        const invoice = generateInvoiceData(lot, bid, auction, isBuyerView);
        setInvoiceData(invoice);
        setShowInvoiceModal(true);
    };

    const auctionColumns = [
        {
            title: 'Auction',
            dataIndex: 'auction_title',
            key: 'auction_title',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: '12px', minWidth: '250px' }}>
                    <Input
                        placeholder="Search auction title"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ marginBottom: 12 }}
                        prefix={<SearchOutlined />}
                        allowClear
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button
                            onClick={() => {
                                if (clearFilters) clearFilters();
                                confirm();
                            }}
                            size="small"
                        >
                            Reset
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => confirm()}
                            size="small"
                        >
                            Search
                        </Button>
                    </div>
                </div>
            ),
            onFilter: (value, record) =>
                record.auction_title?.toLowerCase().includes(value.toLowerCase()),
            filterIcon: (filtered) => (
                <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            ),
            render: (text, record) => (
                <Tooltip title={text} placement="top">
                    <a 
                        href={record.auction_slug ? `/auctions/${record.auction_slug}` : '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                        display: 'inline-block',
                        maxWidth: '250px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: '#1890ff',
                            textDecoration: 'none',
                            cursor: record.auction_slug ? 'pointer' : 'default'
                        }}
                        onMouseEnter={(e) => {
                            if (record.auction_slug) {
                                e.target.style.textDecoration = 'underline';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.textDecoration = 'none';
                        }}
                        onClick={(e) => {
                            if (!record.auction_slug) {
                                e.preventDefault();
                            }
                        }}
                    >
                        {text}
                    </a>
                </Tooltip>
            )
        },
        {
            title: 'Total Lots',
            dataIndex: 'total_lots',
            key: 'total_lots',
            sorter: (a, b) => a.total_lots - b.total_lots,
            render: (count) => (
                <Badge count={count || 0} showZero style={{ backgroundColor: '#1890ff' }} />
            )
        },
        {
            title: 'Start Time',
            dataIndex: 'auction_start_time',
            key: 'auction_start_time',
            sorter: (a, b) => {
                const timeA = a.auction_start_time ? new Date(a.auction_start_time).getTime() : 0;
                const timeB = b.auction_start_time ? new Date(b.auction_start_time).getTime() : 0;
                return timeA - timeB;
            },
            render: (date) => date ? (
                <Tag>{moment(date).format('MMM D, YYYY h:mm A')}</Tag>
            ) : (
                <Tag>N/A</Tag>
            )
        },
        {
            title: 'Created At',
            dataIndex: 'auction_created_at',
            key: 'auction_created_at',
            sorter: (a, b) => {
                const timeA = a.auction_created_at ? new Date(a.auction_created_at).getTime() : 0;
                const timeB = b.auction_created_at ? new Date(b.auction_created_at).getTime() : 0;
                return timeA - timeB;
            },
            render: (date) => date ? (
                <Tag>{moment(date).format('MMM D, YYYY h:mm A')}</Tag>
            ) : (
                <Tag>N/A</Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button 
                    type="link"
                    size='small'
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAuction(record);
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    View Lots
                </Button>
            )
        }
    ];

    // Create lot columns with access to auctions data
    const createLotColumns = (sentAuctions, receivedAuctions) => [
        {
            title: 'Lot',
            dataIndex: 'lot_title',
            key: 'lot_title',
            render: (text, record) => {
                const lotUrl = record.lot_slug && record.auction_slug 
                    ? `/auctions/${record.auction_slug}/lots/${record.lot_slug}` 
                    : '#';
                
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <Tooltip title={text} placement="top">
                            <a 
                                href={lotUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ 
                                    display: 'inline-block',
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    color: '#1890ff',
                                    textDecoration: 'none',
                                    cursor: (record.lot_slug && record.auction_slug) ? 'pointer' : 'default'
                                }}
                                onMouseEnter={(e) => {
                                    if (record.lot_slug && record.auction_slug) {
                                        e.target.style.textDecoration = 'underline';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.textDecoration = 'none';
                                }}
                                onClick={(e) => {
                                    if (!record.lot_slug || !record.auction_slug) {
                                        e.preventDefault();
                                    }
                                }}
                            >
                                {text}
                            </a>
                        </Tooltip>
                        {record.has_start_bid && (
                            <Tag color="blue" icon={<HistoryOutlined />} style={{ margin: 0 }}>
                                Started Lot
                            </Tag>
                        )}
                        {record.has_vendor_bid && (
                            <Tag color="purple" icon={<TagOutlined />} style={{ margin: 0 }}>
                                Vendor Bid
                            </Tag>
                        )}
                    </div>
                );
            },
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: '12px', minWidth: '250px' }}>
                    <Input
                        placeholder="Search lot title"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ marginBottom: 12 }}
                        prefix={<SearchOutlined />}
                        allowClear
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button
                            onClick={() => {
                                if (clearFilters) clearFilters();
                                confirm();
                            }}
                            size="small"
                        >
                            Reset
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => confirm()}
                            size="small"
                        >
                            Search
                        </Button>
                    </div>
                </div>
            ),
            onFilter: (value, record) =>
                record.lot_title?.toLowerCase().includes(value.toLowerCase()),
            filterIcon: (filtered) => (
                <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            )
        },
        {
            title: 'Starting Price',
            dataIndex: 'starting_price',
            key: 'starting_price',
            sorter: (a, b) => parseFloat(a.starting_price || 0) - parseFloat(b.starting_price || 0),
            render: (price) => (
                <Tag color="orange">PKR {parseFloat(price || 0).toFixed(2)}</Tag>
            )
        },
        {
            title: 'Current Price',
            dataIndex: 'current_price',
            key: 'current_price',
            sorter: (a, b) => parseFloat(a.current_price || 0) - parseFloat(b.current_price || 0),
            render: (price) => (
                <Tag color="green">PKR {parseFloat(price || 0).toFixed(2)}</Tag>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            filters: activeTab === 'received' 
                ? [
                    { text: 'Sold', value: 'sold' },
                    { text: 'Cancelled', value: 'cancelled' },
                ]
                : [
                    { text: 'Won', value: 'won' },
                    { text: 'Lost', value: 'lost' },
                    { text: 'Cancelled', value: 'cancelled' },
                ],
            onFilter: (value, record) => {
                if (value === 'cancelled') {
                    return record.status === 'cancelled';
                }
                if (activeTab === 'received') {
                    // For received bids: "Sold" means closed with a winning bid
                    if (value === 'sold') {
                        return record.status === 'closed' && record.bids.some(bid => bid.is_winning_bid);
                    }
                } else {
                    // For sent bids: "Won" means closed with winning bid, "Lost" means closed without winning bid
                    if (value === 'won') {
                        return record.status === 'closed' && record.bids.some(bid => bid.is_winning_bid);
                    }
                    if (value === 'lost') {
                        return record.status === 'closed' && record.bids.some(bid => bid.is_lost_bid);
                    }
                }
                return false;
            },
            render: (status, record) => {
                if (status === 'cancelled') {
                    return <Tag color="purple">Cancelled</Tag>;
                }
                if (status === 'closed') {
                    const winningBid = record.bids.find(bid => bid.is_winning_bid);
                    const lostBid = record.bids.find(bid => bid.is_lost_bid);
                    
                    // For received bids (auction owner view)
                    if (activeTab === 'received') {
                        return <Tag color="green">Sold</Tag>;
                    }
                    
                    // For sent bids (bidder view)
                    return (
                        <div>
                            {winningBid ? (
                                <Tag color="green">Won</Tag>
                            ) : lostBid ? (
                                <Tag color="red">Lost</Tag>
                            ) : (
                                <Tag color="blue">Active</Tag>
                            )}
                        </div>
                    );
                }
                return <Tag color="blue">Active</Tag>;
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                const winningBid = record.bids.find(bid => bid.is_winning_bid);
                const isWon = activeTab === 'sent' && record.status === 'closed' && winningBid;
                const isSold = activeTab === 'received' && record.status === 'closed' && winningBid;
                
                return (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <Button 
                            type="link"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedLot(record);
                            }}
                        >
                            View Bids
                        </Button>
                        {isWon && (
                            <>
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // Get seller info from bid or record
                                        const sellerData = winningBid?.seller || record?.seller;
                                        if (sellerData) {
                                            setSelectedSeller(sellerData);
                                        } else {
                                            message.warning('Seller information not available');
                                        }
                                    }}
                                >
                                    Seller Details
                                </Button>
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<FilePdfOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // Find the auction info from the grouped data
                                        const auction = sentAuctions?.find(a => 
                                            Object.values(a.lots || {}).some(l => l.lot_id === record.lot_id)
                                        );
                                        // Ensure seller info is included in the winning bid if not already present
                                        const bidWithSeller = {
                                            ...winningBid,
                                            seller: winningBid?.seller || record?.seller || null
                                        };
                                        handleGenerateInvoice(record, bidWithSeller, auction, true);
                                    }}
                                    style={{ color: '#1890ff' }}
                                >
                                    Invoice
                                </Button>
                            </>
                        )}
                        {isSold && (
                            <>
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedWinner(record);
                                    }}
                                >
                                    Winner Details
                                </Button>
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<FilePdfOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // Find the auction info from the grouped data
                                        const auction = receivedAuctions?.find(a => 
                                            Object.values(a.lots || {}).some(l => l.lot_id === record.lot_id)
                                        );
                                        handleGenerateInvoice(record, winningBid, auction, false);
                                    }}
                                    style={{ color: '#52c41a' }}
                                >
                                    Invoice
                                </Button>
                            </>
                        )}
                    </div>
                );
            }
        }
    ];

    const bidColumns = [
        {
            title: 'Bid Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (value, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span>PKR {parseFloat(value).toFixed(2)}</span>
                    {record.is_start_bid && (
                        <Tag color="blue" icon={<HistoryOutlined />} style={{ margin: 0 }}>
                            Start Bid
                        </Tag>
                    )}
                    {record.is_vendor_bid && (
                        <Tag color="purple" icon={<TagOutlined />} style={{ margin: 0 }}>
                            Vendor Bid
                        </Tag>
                    )}
                </div>
            )
        },
        {
            title: 'Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => moment(date).format('MMM D, YYYY h:mm A')
        }
    ];

    const renderBidHistory = () => {
        // Group bids by auction
        const groupBidsByAuction = (bids) => {
            return bids?.reduce((acc, bid) => {
                if (!acc[bid.auction_id]) {
                    acc[bid.auction_id] = {
                        auction_id: bid.auction_id,
                        auction_title: bid.auction_title,
                        auction_slug: bid.auction_slug,
                        auction_start_time: bid.auction_start_time,
                        auction_created_at: bid.auction_created_at,
                        total_lots: 0,
                        lots: {}
                    };
                }
                
                if (!acc[bid.auction_id].lots[bid.lot_id]) {
                    acc[bid.auction_id].lots[bid.lot_id] = {
                        lot_id: bid.lot_id,
                        lot_title: bid.lot_title,
                        lot_slug: bid.lot_slug,
                        auction_slug: bid.auction_slug,
                        starting_price: bid.starting_price,
                        current_price: bid.current_price,
                        status: bid.status,
                        bids: [],
                        // Store seller info from the first bid (should be same for all bids on same lot)
                        seller: bid.seller || null,
                        // Track if this lot has owner bids
                        has_start_bid: false,
                        has_vendor_bid: false
                    };
                    acc[bid.auction_id].total_lots++;
                }
                
                // Track if lot has start bid or vendor bid
                if (bid.is_start_bid) {
                    acc[bid.auction_id].lots[bid.lot_id].has_start_bid = true;
                }
                if (bid.is_vendor_bid) {
                    acc[bid.auction_id].lots[bid.lot_id].has_vendor_bid = true;
                }
                
                // Push bid with all its data including seller info and owner bid flags
                acc[bid.auction_id].lots[bid.lot_id].bids.push({
                    ...bid,
                    is_start_bid: bid.is_start_bid || false,
                    is_vendor_bid: bid.is_vendor_bid || false
                });
                
                return acc;
            }, {});
        };

        // Sort each lot's bids newest-first, and sort the auctions themselves by
        // when they most recently happened (newest auction first)
        const sortAuctionsAndBids = (auctions) => {
            const withSortedBids = auctions.map(auction => ({
                ...auction,
                lots: Object.fromEntries(
                    Object.entries(auction.lots).map(([lotId, lot]) => [
                        lotId,
                        { ...lot, bids: [...lot.bids].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) }
                    ])
                )
            }));

            return withSortedBids.sort((a, b) =>
                new Date(b.auction_start_time || b.auction_created_at) - new Date(a.auction_start_time || a.auction_created_at)
            );
        };

        const sentAuctions = sortAuctionsAndBids(Object.values(groupBidsByAuction(sentBids) || {}));
        const receivedAuctions = sortAuctionsAndBids(Object.values(groupBidsByAuction(receivedBids) || {}));
        const lotColumns = createLotColumns(sentAuctions, receivedAuctions);

        // Rank the selected lot's bidders (highest first) so we can show the
        // winner plus 2nd/3rd place backups in the Winner Information modal
        const rankedBidders = selectedWinner ? getRankedBidders(selectedWinner.bids) : [];
        const winningBidInfo = rankedBidders[0] || null;
        const backupBidders = rankedBidders.slice(1, 3);

        return (
            <>
                <Tabs defaultActiveKey="sent" onChange={handleTabChange}>
                    <TabPane tab="My Sent Bids" key="sent">
                        {!selectedAuction ? (
                            <Table
                                columns={auctionColumns}
                                dataSource={sentAuctions}
                                loading={loading}
                                rowKey="auction_id"
                                className="universal-table"
                                scroll={{ x: 900 }}
                                pagination={{
                                    showQuickJumper: true,
                                    showTotal: (total, range) => 
                                        `${range[0]}-${range[1]} of ${total} auctions`,
                                    pageSizeOptions: ['10', '20', '50']
                                }}
                            />
                        ) : (
                            <>
                                <Button 
                                    icon={<ArrowLeftOutlined />} 
                                    onClick={() => setSelectedAuction(null)}
                                    style={{ marginBottom: 16 }}
                                >
                                    Back to Auctions
                                </Button>
                                <Table
                                    columns={lotColumns}
                                    dataSource={Object.values(selectedAuction.lots)}
                                    loading={loading}
                                    rowKey="lot_id"
                                    className="universal-table"
                                    scroll={{ x: 800 }}
                                    pagination={{
                                        showQuickJumper: true,
                                        showTotal: (total, range) => 
                                            `${range[0]}-${range[1]} of ${total} lots`,
                                        pageSizeOptions: ['10', '20', '50']
                                    }}
                                />
                            </>
                        )}
                        {selectedLot && (
                            <Modal
                                title={`Bid History - ${selectedLot.lot_title}`}
                                open={!!selectedLot}
                                onCancel={() => setSelectedLot(null)}
                                footer={null}
                                width={800}
                            >
                                <Table
                                    columns={bidColumns}
                                    dataSource={selectedLot.bids}
                                    loading={loading}
                                    rowKey="id"
                                    className="universal-table"
                                    scroll={{ x: 400 }}
                                    pagination={{
                                        showTotal: (total, range) =>
                                            `${range[0]}-${range[1]} of ${total} bids`,
                                        pageSizeOptions: ['10', '20', '50']
                                    }}
                                />
                            </Modal>
                        )}
                    </TabPane>
                    <TabPane tab="Received Bids" key="received">
                        {!selectedAuction ? (
                            <Table
                                columns={auctionColumns}
                                dataSource={receivedAuctions}
                                loading={loading}
                                rowKey="auction_id"
                                className="universal-table"
                                scroll={{ x: 900 }}
                                pagination={{
                                    showTotal: (total, range) => 
                                        `${range[0]}-${range[1]} of ${total} auctions`,
                                    pageSizeOptions: ['10', '20', '50']
                                }}
                            />
                        ) : (
                            <>
                                <Button 
                                    icon={<ArrowLeftOutlined />} 
                                    onClick={() => setSelectedAuction(null)}
                                    style={{ marginBottom: 16 }}
                                >
                                    Back to Auctions
                                </Button>
                                <Table
                                    columns={lotColumns}
                                    dataSource={Object.values(selectedAuction.lots)}
                                    loading={loading}
                                    rowKey="lot_id"
                                    className="universal-table"
                                    scroll={{ x: 800 }}
                                    pagination={{
                                        showSizeChanger: true,
                                        showQuickJumper: true,
                                        showTotal: (total, range) => 
                                            `${range[0]}-${range[1]} of ${total} lots`,
                                        pageSizeOptions: ['10', '20', '50']
                                    }}
                                />
                            </>
                        )}
                        {selectedLot && (
                            <Modal
                                title={`Bids Received - ${selectedLot.lot_title}`}
                                open={!!selectedLot}
                                onCancel={() => setSelectedLot(null)}
                                footer={null}
                                width={800}
                            >
                                <Table
                                    columns={bidColumns}
                                    dataSource={selectedLot.bids}
                                    loading={loading}
                                    rowKey="id"
                                    className="universal-table"
                                    scroll={{ x: 400 }}
                                    pagination={{
                                        showQuickJumper: true,
                                        showTotal: (total, range) => 
                                            `${range[0]}-${range[1]} of ${total} bids`,
                                        pageSizeOptions: ['10', '20', '50']
                                    }}
                                />
                            </Modal>
                        )}
                    </TabPane>
                </Tabs>
                {selectedWinner && (
                    <Modal
                        title="Winner Information"
                        open={!!selectedWinner}
                        onCancel={() => setSelectedWinner(null)}
                        footer={[
                            <Button key="close" onClick={() => setSelectedWinner(null)}>
                                Close
                            </Button>
                        ]}
                    >
                        <div style={{ padding: '20px' }}>
                            {winningBidInfo && (
                                <div style={{
                                    background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
                                    border: '2px solid #52c41a',
                                    borderRadius: 10,
                                    padding: '16px 18px',
                                    boxShadow: '0 4px 12px rgba(82, 196, 26, 0.2)'
                                }}>
                                    <h3 style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        color: '#389e0d',
                                        marginBottom: 12
                                    }}>
                                        <TrophyOutlined /> Winner Details
                                    </h3>
                                    <p>
                                        <strong>Name:</strong>{' '}
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                            {winningBidInfo.first_name} {winningBidInfo.last_name || 'Anonymous'}
                                            {(winningBidInfo.is_verified || winningBidInfo.seller_trust_badge || winningBidInfo.buyer_protection_plus) && (
                                                <UserBadges user={winningBidInfo} iconSize={16} />
                                            )}
                                        </span>
                                    </p>
                                    {winningBidInfo.bidder_email && (
                                        <p><strong>Email:</strong> {winningBidInfo.bidder_email}</p>
                                    )}
                                    <p>
                                        <strong>Phone:</strong>{' '}
                                        {winningBidInfo.bidder_phone || winningBidInfo.phone_number || 'N/A'}
                                    </p>
                                    <p style={{ fontSize: '16px' }}>
                                        <strong>Winning Bid Amount:</strong>{' '}
                                        <span style={{ color: '#389e0d', fontWeight: 700 }}>
                                            PKR {parseFloat(winningBidInfo.amount).toFixed(2)}
                                        </span>
                                    </p>
                                    <p style={{ marginBottom: 0 }}>
                                        <strong>Bid Date:</strong> {moment(winningBidInfo.created_at).format('MMM D, YYYY h:mm A')}
                                    </p>
                                </div>
                            )}
                            <Divider />
                            <Text type="secondary">
                                This is the winner of the lot from your auction.
                            </Text>

                            {backupBidders.length > 0 && (
                                <>
                                    <Divider />
                                    <h3>Backup Bidders</h3>
                                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                                        If the winner doesn't follow through, here are the next-highest bidders you can contact instead.
                                    </Text>
                                    {backupBidders.map((bidder, index) => (
                                        <div
                                            key={bidder.id}
                                            style={{
                                                padding: '12px 14px',
                                                marginBottom: 10,
                                                background: '#fafafa',
                                                borderRadius: 8,
                                                border: '1px solid #f0f0f0'
                                            }}
                                        >
                                            <p style={{ marginBottom: 4 }}>
                                                <Tag color={index === 0 ? 'gold' : 'default'} style={{ marginRight: 8 }}>
                                                    {index === 0 ? '2nd Highest' : '3rd Highest'}
                                                </Tag>
                                                <strong>{bidder.first_name} {bidder.last_name || 'Anonymous'}</strong>
                                            </p>
                                            {bidder.bidder_email && (
                                                <p style={{ marginBottom: 2 }}><strong>Email:</strong> {bidder.bidder_email}</p>
                                            )}
                                            <p style={{ marginBottom: 2 }}>
                                                <strong>Phone:</strong>{' '}
                                                {bidder.bidder_phone || bidder.phone_number || 'N/A'}
                                            </p>
                                            <p style={{ marginBottom: 0 }}>
                                                <strong>Bid Amount:</strong> PKR {parseFloat(bidder.amount).toFixed(2)}
                                            </p>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    </Modal>
                )}
                {selectedSeller && (
                    <Modal
                        title="Seller Information"
                        open={!!selectedSeller}
                        onCancel={() => setSelectedSeller(null)}
                        footer={[
                            <Button key="close" onClick={() => setSelectedSeller(null)}>
                                Close
                            </Button>
                        ]}
                    >
                        <div style={{ padding: '20px' }}>
                            <h3>Seller Details</h3>
                            <p>
                                <strong>Name:</strong>{' '}
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                    {selectedSeller.first_name || selectedSeller.last_name
                                    ? `${selectedSeller.first_name || ''} ${selectedSeller.last_name || ''}`.trim()
                                        : 'N/A'}
                                    {selectedSeller && (selectedSeller.is_verified || selectedSeller.seller_trust_badge || selectedSeller.buyer_protection_plus) && (
                                        <UserBadges user={selectedSeller} iconSize={16} />
                                    )}
                                </span>
                            </p>
                            {selectedSeller.email && (
                                <p><strong>Email:</strong> {selectedSeller.email}</p>
                            )}
                            {selectedSeller.phone_number && (
                                <p><strong>Phone:</strong> {selectedSeller.phone_number}</p>
                            )}
                            <Divider />
                            <Text type="secondary">
                                This is the auction organizer who sold the lot to you.
                            </Text>
                        </div>
                    </Modal>
                )}
                {showInvoiceModal && invoiceData && (
                    <Modal
                        title={
                            <Space>
                                <FilePdfOutlined />
                                <span style={{ fontSize: windowWidth <= 576 ? '14px' : '16px' }}>
                                    Invoice - {invoiceData.invoiceNumber}
                                </span>
                            </Space>
                        }
                        open={showInvoiceModal}
                        onCancel={() => {
                            setShowInvoiceModal(false);
                            setInvoiceData(null);
                        }}
                        footer={[
                            <Button key="cancel" onClick={() => {
                                setShowInvoiceModal(false);
                                setInvoiceData(null);
                            }}>
                                Close
                            </Button>,
                            <Button 
                                key="print" 
                                type="primary" 
                                icon={<PrinterOutlined />}
                                onClick={() => {
                                    // Small delay to ensure ref is attached
                                    setTimeout(() => {
                                        handlePrint();
                                    }, 100);
                                }}
                            >
                                Print / Save as PDF
                            </Button>
                        ]}
                        width={windowWidth <= 768 ? (windowWidth <= 576 ? '95%' : '90%') : 900}
                        style={{ top: windowWidth <= 576 ? 10 : 20 }}
                        bodyStyle={{ 
                            padding: 0, 
                            maxHeight: windowWidth <= 576 ? '85vh' : '80vh', 
                            overflow: 'auto' 
                        }}
                        className="invoice-modal"
                    >
                        <div style={{ 
                            padding: windowWidth <= 576 ? '10px' : '20px',
                            boxSizing: 'border-box'
                        }}>
                            <Invoice ref={invoiceRef} invoiceData={invoiceData} />
                        </div>
                    </Modal>
                )}
            </>
        );
    };

    const renderInstructions = () => (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <BookOutlined style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }} />
                <Title level={4} style={{ margin: 0 }}>Help</Title>
            </div>


            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card 
                    title={
                        <Space>
                            <EyeOutlined style={{ color: '#1890ff' }} />
                            <span>My Sent Bids</span>
                        </Space>
                    }
                    bordered={false}
                    style={{ backgroundColor: '#f0f7ff' }}
                >
                    <Space direction="vertical" size="middle">
                        <Space>
                            <TagOutlined style={{ color: '#1890ff' }} />
                            <Text>View all auctions where you have placed bids</Text>
                        </Space>
                        <Space>
                            <HistoryOutlined style={{ color: '#1890ff' }} />
                            <Text>Click 'View Lots' to see your bids for specific lots</Text>
                        </Space>
                        <Space>
                            <TagOutlined style={{ color: '#1890ff' }} />
                            <Text>Track your winning and losing bids with color-coded status tags</Text>
                        </Space>
                        <Space>
                            <HistoryOutlined style={{ color: '#1890ff' }} />
                            <Text>View detailed bid history for each lot</Text>
                        </Space>
                    </Space>
                </Card>

                <Card 
                    title={
                        <Space>
                            <MonitorOutlined style={{ color: '#52c41a' }} />
                            <span>Received Bids</span>
                        </Space>
                    }
                    bordered={false}
                    style={{ backgroundColor: '#f6ffed' }}
                >
                    <Space direction="vertical" size="middle">
                        <Space>
                            <UserOutlined style={{ color: '#52c41a' }} />
                            <Text>Monitor all bids received on your auction lots</Text>
                        </Space>
                        <Space>
                            <HistoryOutlined style={{ color: '#52c41a' }} />
                            <Text>View bid history and current status of each lot</Text>
                        </Space>
                        <Space>
                            <TrophyOutlined style={{ color: '#52c41a' }} />
                            <Text>Access winner details for winning bids</Text>
                        </Space>
                        <Space>
                            <CloseCircleOutlined style={{ color: '#52c41a' }} />
                            <Text>Track lot status (Active, Sold, Cancelled)</Text>
                        </Space>
                    </Space>
                </Card>
            </Space>
        </Space>
    );

    return (
        <DashboardLayout style={{ padding: '24px' }}>
            <PageHeader
                title="Bid History"
                description="Track and manage your bidding activity across all auctions"
                buttonText="Help"
                buttonIcon={<QuestionCircleOutlined />}
                onButtonClick={() => setShowInstructions(true)}
            />
            {renderBidHistory()}

            <Modal
                title={null}
                open={showInstructions}
                onCancel={() => setShowInstructions(false)}
                footer={[
                    <Button 
                        key="close" 
                        type="primary" 
                        onClick={() => setShowInstructions(false)}
                        size="large"
                        style={{ minWidth: 120 }}
                    >
                        Got it!
                    </Button>
                ]}
                width={800}
                bodyStyle={{ padding: '24px' }}
                style={{ top: 20 }}
            >
                {renderInstructions()}
            </Modal>
        </DashboardLayout>
    );
};

export default UserAnalytics; 